import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Link } from "react-router-dom";
import SEO from "../../SEO";

const OurTools = () => {
  const scrollContainerRef = useRef(null);

  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  // fetch data
  useEffect(() => {
    const fetchToolsAndCategories = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/tools`
        );
        const toolsData = response.data;

        // Map through the data to extract tools and categories
        const extractedTools = toolsData.data.map((tool) => ({
          id: tool.id,
          ...(tool.attributes || tool),
        }));

        setTools(extractedTools);

        // Extract unique categories from the tools
        const uniqueCategories = [
          ...new Set(extractedTools.map((tool) => tool.category)),
        ];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error("Error fetching tools: ", error);
      }
    };

    fetchToolsAndCategories();
  }, []);

  const topDownloads = tools
    .sort((a, b) => parseInt(b.downloads) - parseInt(a.downloads))
    .slice(0, 5);

  const filteredTools = selectedCategory
    ? tools.filter((tool) => tool.category === selectedCategory)
    : tools;

  const scrollLeft = () => {
    scrollContainerRef.current.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollContainerRef.current.scrollBy({ left: 300, behavior: "smooth" });
  };

  return (
  <>
   <SEO
      title="Explore Ethical Hacking & Programming – Free Online Learning"
      description="Discover free ethical hacking and programming courses at PPDDEH. Learn cybersecurity, coding, and more with expert tutorials and hands-on practice!"
      url="https://www.ppddeh.com/our-tools"
    />
  <div className="max-w-7xl mx-auto px-6 py-12">
      {/* Search Bar */}
      <div className="flex justify-center mb-12">
        <div className="relative w-full max-w-md">
          <input
            type="text"
            placeholder="Search tools..."
            className="w-full py-2 pl-4 pr-12 border border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">
            <i className="fas fa-search"></i>
          </button>
        </div>
      </div>

      {/* Trending Content Section */}
      <div className="mb-12 mt-[70px]">
        <h2 className="text-2xl font-semibold text-center mb-6">
          Trending Content
        </h2>

        <div className="flex flex-wrap justify-center gap-4">
          <button className="px-6 py-2 w-[170px] bg-white text-blue-500 border border-black rounded-lg hover:bg-blue-100 transition">
            AI
          </button>
          <button className="px-6 py-2 w-[170px] bg-white text-blue-500 border border-black rounded-lg hover:bg-blue-100 transition">
            Cybersecurity
          </button>
          <button className="px-6 py-2 w-[170px] bg-white text-blue-500 border border-black rounded-lg hover:bg-blue-100 transition">
            Ethical Hacking
          </button>
          <button className="px-6 py-2 w-[170px] bg-white text-blue-500 border border-black rounded-lg hover:bg-blue-100 transition">
            Ganion Gaming
          </button>
        </div>
      </div>

      {/* Trending Tools Section */}
      <div className="mb-12 mt-[100px]">
        <h2 className="text-2xl font-semibold text-center mb-6">
          Trending Tools
        </h2>

        {/* Nav Bar for Filtering */}
        <div className="flex flex-wrap mb-8 justify-start gap-4">
          <button
            className={`px-2 py-2 transition ${
              selectedCategory === ""
                ? "text-blue-500 underline"
                : "text-gray-500"
            }`}
            onClick={() => setSelectedCategory("")}
          >
            All
          </button>

          {categories.map((category, index) => (
            <button
              key={index}
              className={`px-2 py-2 transition ${
                selectedCategory === category
                  ? "text-blue-500 underline"
                  : "text-gray-500"
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Tool Cards Scrollable View */}
        <div className="relative">
          <button
            className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-blue-500 p-2 rounded-full shadow-lg hover:bg-gray-300"
            onClick={scrollLeft}
          >
            <FaChevronLeft size={20} />
          </button>

          <div
            ref={scrollContainerRef}
            className={`flex overflow-x-auto gap-6 ${
              selectedCategory === "" ? "justify-start" : "justify-center"
            }`}
            style={{
              scrollBehavior: "smooth",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {filteredTools.map((tool) => (
              <Link
                key={tool.slug}
                to={`/our-tools/${tool.slug}`}
                className="bg-white border border-gray-300 rounded-lg w-40 p-4 cursor-pointer flex-none"
              >
                <div className="w-full h-32 mb-4 flex justify-center items-center">
                  <img
                    src={tool.image}
                    alt={tool.name}
                    className="object-contain h-full"
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{tool.ratings} ★</span>

                  {tool.price ? (
                    <span className="text-gray-600">₹ {tool.price}</span>
                  ) : (
                    <span className="text-gray-600">Free</span>
                  )}
                </div>
                <h4 className="text-center text-lg font-semibold mt-2">
                  {tool.name}
                </h4>
              </Link>
            ))}
          </div>

          <button
            className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-blue-500 p-2 rounded-full shadow-lg hover:bg-gray-300"
            onClick={scrollRight}
          >
            <FaChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Top Downloads Section */}
      <div>
        <h2 className="text-2xl font-semibold text-center mb-6 mt-[100px]">
          Top Downloads
        </h2>

        <div className="border border-gray-300 rounded-lg p-6">
          {topDownloads.map((tool, index) => (
            <div
              key={index}
              className="flex flex-col sm:flex-row items-center sm:justify-between py-4 px-2 hover:bg-gray-700 transition rounded-lg"
            >
              {/* Index and Image */}
              <div className="flex items-center gap-4 w-full sm:w-1/5 mb-4 sm:mb-0">
                <span className="font-semibold text-lg text-gray-500">
                  {index + 1}.
                </span>
                <img
                  src={tool.image}
                  alt={tool.name}
                  className="w-12 h-12 object-contain rounded-md"
                />
              </div>

              {/* Name */}
              <div className="w-full sm:w-1/5 mb-4 sm:mb-0">
                <h4 className="text-lg font-semibold text-gray-500">
                  {tool.name}
                </h4>
              </div>

              {/* Price */}
              <div className="w-full sm:w-1/5 text-center mb-4 sm:mb-0">
                {tool.price ? (
                  <span className="text-gray-600">₹ {tool.price}</span>
                ) : (
                  <span className="text-gray-600">Free</span>
                )}
              </div>

              {/* Ratings */}
              <div className="w-full sm:w-1/5 text-center mb-4 sm:mb-0">
                <span className="text-sm text-gray-400">{tool.ratings} ★</span>
              </div>

              {/* Downloads */}
              <div className="w-full sm:w-1/5 text-center mb-4 sm:mb-0">
                <span className="text-sm text-gray-400">{tool.downloads}</span>
              </div>

              {/* Button */}
              <div className="w-full sm:w-1/5 text-center sm:text-right">
                <button className="px-4 py-2 bg-blue-400 text-white text-sm rounded-lg hover:bg-blue-600 transition">
                  Download
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </>
  );
};

export default OurTools;
